import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaCrown, FaTimes, FaExclamationTriangle, FaEnvelope } from "react-icons/fa";
import { socket } from "../socket";

// Titles only — descriptions now come from the backend's customer-friendly
// `message` field so the copy stays in one place and matches what the
// outreach worker writes into per-exec lastError.
const REASON_TITLE = {
  message_exceeded: "Message Limit Reached",
  QUOTA_EXCEEDED: "Message Limit Reached",
  whatsappConnection_exceeded: "Connection Limit Reached",
  feature_not_available: "Feature Not Available",
  no_subscription: "No Active Subscription",
  NO_ACTIVE_SUBSCRIPTION: "No Active Subscription",
  expired: "Subscription Expired",
};

const FALLBACK_DESCRIPTION = "You've reached a limit on your current plan.";

// Debounce window for the live `quota:exceeded` socket event. The backend
// emits one socket event per quota-blocked outreach exec, so a campaign
// hitting its cap on 40 leads would fire the modal 40 times. Suppress
// re-fires inside the window — one modal per minute is plenty.
const SOCKET_DEBOUNCE_MS = 60 * 1000;

export default function QuotaExceededModal() {
  const [visible, setVisible] = useState(false);
  const [detail, setDetail] = useState(null);
  const lastSocketShowRef = useRef(0);
  const navigate = useNavigate();

  const open = useCallback((data) => {
    setDetail(data || {});
    setVisible(true);
  }, []);

  useEffect(() => {
    // HTTP path: axiosInstance dispatches `quota-exceeded` on any 402.
    const onHttp = (e) => open(e.detail);
    window.addEventListener("quota-exceeded", onHttp);

    // Live path: the outreach worker emits `quota:exceeded` when a long-
    // running campaign exhausts the user's cap mid-flight. Without this,
    // the customer wouldn't see the modal until they next clicked a
    // button that triggered a 402.
    const onSocket = (payload) => {
      const now = Date.now();
      if (now - lastSocketShowRef.current < SOCKET_DEBOUNCE_MS) return;
      lastSocketShowRef.current = now;
      open({
        error: "QUOTA_EXCEEDED",
        reason: `${payload?.feature || "message"}_exceeded`,
        feature: payload?.feature,
        message: payload?.message,
        redirectTo: payload?.redirectTo || "/pricing",
        // No used/total on the socket payload — backend doesn't include them
        // to avoid encoding billing state in a fire-and-forget broadcast.
      });
    };
    socket?.on?.("quota:exceeded", onSocket);

    return () => {
      window.removeEventListener("quota-exceeded", onHttp);
      socket?.off?.("quota:exceeded", onSocket);
    };
  }, [open]);

  if (!visible || !detail) return null;

  // Resolve title — backend's `error` (new) takes precedence over `reason`
  // (back-compat). Fall back to a generic title if neither is recognised.
  const titleKey = detail.error || detail.reason || "no_subscription";
  const title = REASON_TITLE[titleKey] || "Limit Reached";

  // Description: prefer the backend's customer-friendly `message` so the
  // copy stays consistent with what the worker writes into lastError. Only
  // fall back to a generic line if `message` is missing.
  const description = detail.message || FALLBACK_DESCRIPTION;

  // Usage strip (e.g. "1000 / 1000 used") — render only when the backend
  // sent concrete numbers. Skip on the socket-driven path.
  const hasUsage =
    typeof detail.used === "number" &&
    typeof detail.total === "number" &&
    detail.total > 0;

  // Redirect target: backend chooses '/pricing' for no-subscription users
  // and '/settings/billing' for existing subscribers. Fall back to /pricing.
  const redirectTo = detail.redirectTo || "/pricing";

  const close = () => {
    setVisible(false);
    setDetail(null);
  };

  const goToBilling = () => {
    close();
    navigate(redirectTo);
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" onClick={close}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        className="relative bg-surface-card rounded-xl shadow-2xl border border-surface-border w-full max-w-sm animate-modal-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 text-center">
          <button className="absolute top-3 right-3 text-ink-faint hover:text-ink-primary p-1" onClick={close}>
            <FaTimes size={14} />
          </button>

          <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-4">
            <FaExclamationTriangle size={20} className="text-amber-600" />
          </div>

          <h3 className="text-base font-bold text-ink-primary mb-2">{title}</h3>
          <p className="text-sm text-ink-muted mb-4 leading-relaxed">{description}</p>

          {hasUsage && (
            <div className="text-xs text-ink-faint mb-4 px-3 py-2 rounded-lg bg-surface-subtle">
              Used <span className="font-semibold text-ink-primary">{detail.used.toLocaleString()}</span>
              {" / "}
              <span className="font-semibold text-ink-primary">{detail.total.toLocaleString()}</span>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <button onClick={goToBilling} className="btn-primary w-full flex items-center justify-center gap-2">
              <FaCrown size={12} />
              {detail.error === "NO_ACTIVE_SUBSCRIPTION" ? "Choose a Plan" : "Upgrade Plan"}
            </button>
            <a
              href="mailto:support@walytic.com"
              className="btn-secondary w-full flex items-center justify-center gap-2 text-sm"
              onClick={close}
            >
              <FaEnvelope size={11} />
              Contact Support
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
